// Greenhold <PatronageCertificate> — the engraved-parchment resale artifact.
//
// Epic 8 design pass (2026-08): faithful port of the design-tool prototype
// design/claude-design/templates/market-page/PatronageCertificate.dc.html.
// A fixed 320×452 intrinsic artifact rendered with inline styles (mirrors
// the SealedDossier port convention) — parent scales it via the optional
// `width` prop (transform:scale wrapper reserving the correct layout box)
// or its own transform.
//
// Anatomy, top → bottom, all on aged parchment paper (linear-gradient of
// the --gh-parchment ramp), behind a faint oak watermark:
//   * mono "GREENHOLD REGISTRY" eyebrow (gold-600, wide-tracked)
//   * Georgia "Certificate of Patronage" title
//   * 60px tier-accent accent rule
//   * 88px circular portrait — tier-accent glow ring + double-hairline
//     ring, real creator image with an initials fallback
//   * italic Georgia creator name in the tier's main colour
//   * optional creator quote (OMITTED entirely when absent — no placeholder)
//   * optional rotated signature: the creator's real uploaded ink
//     (`signatureUrl`, multiply-blended into the parchment) or the
//     cursive name scrawl (`signed`) as fallback
//   * bottom mono <dl>: serial (always) / dossier / issued / condition —
//     each row rendered ONLY when its value is provided (no placeholders)
//
// The artifact's signature feature is PROCEDURAL CONDITION WEAR — smudges
// (radial-gradients), edge tears (SVG mask holes + stroke paths) and
// hairline cracks, all seeded deterministically from serial+condition. It
// is ported verbatim from the prototype's `renderVals()`. `condition`
// defaults to 0 (mint — no tears/smudges) because the app has no condition
// axis yet; pass a value to exercise the wear + surface the condition row.
//
// Entirely non-interactive: no links or buttons, so it nests safely inside
// the marketplace card's <button> and the buy-modal chrome without a
// nested-interactive a11y regression.

import type { CSSProperties, FC, ReactNode } from "react";
import { useState } from "react";

export type PatronageCertificateTier =
  | "common"
  | "preferred"
  | "premium"
  | "founders";

export interface PatronageCertificateProps {
  tier: PatronageCertificateTier;
  /** Creator display name — rendered italic + used for the initials fallback. */
  name: string;
  /** Registry serial (mono). Also seeds the procedural wear algorithm. */
  serial: string;
  /** Portfolio / drop title for the "dossier" row. Row omitted when absent. */
  dossier?: string;
  /** Issue date string for the "issued" row. Row omitted when absent. */
  issued?: string;
  /** Creator portrait URL. Falls back to initials when absent/unloadable. */
  portraitUrl?: string | null;
  /** Optional creator quote. The whole element is omitted when absent. */
  quote?: string;
  /** 0 (mint) → 1 (ruined). Drives the procedural wear + the condition row.
   *  Default 0. When left undefined the condition row is not rendered. */
  condition?: number;
  /** Ports the rotated signature scrawl. Default false. */
  signed?: boolean;
  /** Creator's uploaded signature-ink image (dark ink on white/transparent).
   *  Rendered in the signature slot, multiply-blended so a white background
   *  melts into the parchment. Falls back to the cursive `signed` scrawl
   *  when absent or unloadable. */
  signatureUrl?: string | null;
  /** When set, renders the 320×452 artifact pre-scaled to this width via a
   *  transform wrapper that reserves the correct (width × 452/320) box. */
  width?: number;
  /** Optional className on the outer wrapper (composition only). */
  className?: string;
}

// Tier palette — main (name/values ink) + accent (rules, glow ring).
// Verbatim from the prototype's `T` table.
const TIER: Record<
  PatronageCertificateTier,
  { main: string; accent: string }
> = {
  common: { main: "#1f2d24", accent: "#7bb796" },
  preferred: { main: "#2a5da0", accent: "#5e8ec5" },
  premium: { main: "#5b21b6", accent: "#8b5cf6" },
  founders: { main: "#c39235", accent: "#e9cf85" },
};

// Faint oak watermark path (viewBox 0 0 100 100) — verbatim from prototype.
const OAK_PATH =
  "M50 10 C41 8 33 13 30 20 C21 19 14 26 15 34 C8 36 5 46 11 52 C8 59 14 67 22 66 C25 73 35 76 41 71 C43 69 44 68 45 66 C46 71 46 76 45 80 C43 86 41 89 38 92 L62 92 C59 89 57 86 55 80 C54 76 54 71 55 66 C56 68 57 69 59 71 C65 76 75 73 78 66 C86 67 92 59 89 52 C95 46 92 36 85 34 C86 26 79 19 70 20 C67 13 59 8 50 10 Z";

const W = 320;
const H = 452;

/** Corner radius of the parchment silhouette (matches the design's
 *  borderRadius:6). Tears never reach the corners (a0 ≥ 24, ends ≤ L−26). */
const CORNER_R = 6;

interface WearResult {
  /** SVG path (in the 320×452 design space) of the torn parchment
   *  SILHOUETTE — the card rectangle with tear notches bitten inward from
   *  the edges. Used three ways: (1) `clip-path` on the parchment fill so
   *  missing pieces are truly TRANSPARENT (nothing bleeds behind them),
   *  (2) a `<path stroke>` so the edge tint FOLLOWS the torn contour instead
   *  of running straight across, (3) `drop-shadow` follows this alpha shape
   *  so the glow hugs the tears too. */
  outline: string;
  /** CSS background: joined radial-gradient smudge spots (or "none"). */
  spots: string;
  /** SVG stroke elements — interior hairline cracks (the torn EDGE is now
   *  the outline stroke, not these). */
  strokeEls: ReactNode[];
  /** Inset shadow blur radius + darkness scale with condition. */
  smudgeBoxShadow: string;
}

type Tear = { edge: "top" | "right" | "bottom" | "left"; a0: number; xy: [number, number][] };

/** Builds the torn-parchment silhouette path (clockwise, rounded corners).
 *  Each edge is walked in the perimeter direction; a tear on that edge is a
 *  jagged bay that dips inward from the straight edge and returns — so the
 *  region outside the path (the bite) is empty/transparent. */
function buildOutline(tears: Tear[]): string {
  const f = (n: number) => n.toFixed(1);
  const pt = (p: [number, number]) => `${f(p[0])} ${f(p[1])}`;
  const R = CORNER_R;
  const on = (e: Tear["edge"]) => tears.filter((t) => t.edge === e);
  // Along-edge order matches the walk direction (bottom/left run backwards,
  // so their tears sort descending + their points reverse).
  const top = on("top").sort((a, b) => a.a0 - b.a0);
  const right = on("right").sort((a, b) => a.a0 - b.a0);
  const bottom = on("bottom").sort((a, b) => b.a0 - a.a0);
  const left = on("left").sort((a, b) => b.a0 - a.a0);

  const c: string[] = [`M${f(R)} 0`];
  for (const t of top) for (const p of t.xy) c.push(`L${pt(p)}`);
  c.push(`L${f(W - R)} 0`, `A${R} ${R} 0 0 1 ${f(W)} ${f(R)}`);
  for (const t of right) for (const p of t.xy) c.push(`L${pt(p)}`);
  c.push(`L${f(W)} ${f(H - R)}`, `A${R} ${R} 0 0 1 ${f(W - R)} ${f(H)}`);
  for (const t of bottom) for (const p of [...t.xy].reverse()) c.push(`L${pt(p)}`);
  c.push(`L${f(R)} ${f(H)}`, `A${R} ${R} 0 0 1 0 ${f(H - R)}`);
  for (const t of left) for (const p of [...t.xy].reverse()) c.push(`L${pt(p)}`);
  c.push(`L0 ${f(R)}`, `A${R} ${R} 0 0 1 ${f(R)} 0`, "Z");
  return c.join(" ");
}

/**
 * Procedural condition wear — ported verbatim from the prototype
 * `renderVals()`. Deterministic: seeded from `serial` + `condition` so the
 * same certificate always weathers identically. `cond` is pre-clamped 0..1.
 */
function computeWear(serial: string, cond: number): WearResult {
  let s = Math.floor(cond * 1e6) + 13;
  for (const ch of serial) s = (s * 31 + ch.charCodeAt(0)) % 233280;
  const rnd = () => (s = (s * 9301 + 49297) % 233280) / 233280;

  // ── smudges ──
  const nSm = Math.round(cond * 6);
  const spots: string[] = [];
  for (let i = 0; i < nSm; i++) {
    const cx = rnd() < 0.5 ? rnd() * 18 : 82 + rnd() * 18;
    const cy = rnd() * 100;
    spots.push(
      `radial-gradient(ellipse ${Math.round(30 + rnd() * 60)}px ${Math.round(
        20 + rnd() * 40,
      )}px at ${cx.toFixed(0)}% ${cy.toFixed(0)}%, rgba(58,47,28,${(
        0.12 +
        cond * 0.4 * rnd()
      ).toFixed(2)}), transparent 70%)`,
    );
  }

  // ── tears (edge notches) + hairline cracks ──
  const toXY = (edge: Tear["edge"], a: number, d: number): [number, number] =>
    edge === "top"
      ? [a, d]
      : edge === "bottom"
        ? [a, H - d]
        : edge === "left"
          ? [d, a]
          : [W - d, a];

  const tears: Tear[] = [];
  const strokeEls: ReactNode[] = [];
  const nT = cond < 0.06 ? 0 : Math.min(6, 1 + Math.floor(cond * 6.5));
  const edges: Tear["edge"][] = ["top", "right", "bottom", "left"];
  for (let i = 0; i < nT; i++) {
    const edge = edges[Math.floor(rnd() * 4) % 4];
    const L = edge === "top" || edge === "bottom" ? W : H;
    const a0 = 24 + rnd() * (L - 90);
    const len = (14 + rnd() * 22) * (0.6 + cond * 0.8);
    const k = 2 + Math.floor(rnd() * 3);
    // Jagged inward profile: endpoints hug the edge (~0), middle points bite
    // in. Deeper at higher condition so worn cards lose real parchment.
    const pts: [number, number][] = [[a0, 0.5 + rnd() * 1.5]];
    for (let j = 1; j <= k; j++)
      pts.push([
        a0 + len * (j / (k + 1)) + (rnd() - 0.5) * 4,
        2 + rnd() * (4 + cond * 10),
      ]);
    pts.push([a0 + len, 0.5 + rnd() * 1.5]);
    tears.push({ edge, a0, xy: pts.map((p) => toXY(edge, p[0], p[1])) });
  }

  const outline = buildOutline(tears);

  const nC = Math.floor(cond * 4);
  for (let i = 0; i < nC; i++) {
    const edge = edges[Math.floor(rnd() * 4) % 4];
    const L = edge === "top" || edge === "bottom" ? W : H;
    const a = 20 + rnd() * (L - 40);
    const p0 = toXY(edge, a, 0);
    const p1 = toXY(edge, a + (rnd() - 0.5) * 6, 4 + rnd() * 6);
    const p2 = toXY(edge, a + (rnd() - 0.5) * 10, 9 + rnd() * 8);
    strokeEls.push(
      <path
        key={"c" + i}
        d={`M${p0[0].toFixed(1)} ${p0[1].toFixed(1)}L${p1[0].toFixed(
          1,
        )} ${p1[1].toFixed(1)}L${p2[0].toFixed(1)} ${p2[1].toFixed(1)}`}
        stroke="rgba(58,47,28,0.4)"
        strokeWidth={0.9}
        fill="none"
      />,
    );
  }

  const smudgeBoxShadow = `inset 0 0 ${
    8 + Math.round(cond * 38)
  }px rgba(58,47,28,${(0.08 + cond * 0.5).toFixed(2)})`;

  return {
    outline,
    spots: spots.join(",") || "none",
    strokeEls,
    smudgeBoxShadow,
  };
}

/** Circular portrait with tier-accent glow ring + double hairline ring;
 *  real image with an initials fallback (mirrors the design's image-slot). */
function Portrait({
  accent,
  main,
  portraitUrl,
  initial,
}: {
  accent: string;
  main: string;
  portraitUrl?: string | null;
  initial: string;
}) {
  const [errored, setErrored] = useState(false);
  const showImage = !!portraitUrl && !errored;
  return (
    <div style={{ position: "relative", width: 88, height: 88 }}>
      {/* tier-accent glow ring */}
      <div
        aria-hidden="true"
        style={{
          position: "absolute",
          inset: -2,
          borderRadius: "50%",
          boxShadow: `0 0 10px ${accent}8c, 0 0 22px ${accent}4d`,
        }}
      />
      {/* double hairline ring holding the portrait */}
      <div
        style={{
          position: "absolute",
          inset: 0,
          borderRadius: "50%",
          border: "1.5px solid rgba(122,90,29,0.65)",
          outline: "1px solid rgba(122,90,29,0.35)",
          outlineOffset: 3,
          padding: 3,
          boxSizing: "border-box",
          background: "rgba(253,251,245,0.5)",
        }}
      >
        <div
          style={{
            width: "100%",
            height: "100%",
            borderRadius: "50%",
            overflow: "hidden",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
          }}
        >
          {showImage ? (
            <img
              src={portraitUrl ?? undefined}
              alt=""
              loading="lazy"
              onError={() => setErrored(true)}
              style={{ width: "100%", height: "100%", objectFit: "cover" }}
            />
          ) : (
            <span
              aria-hidden="true"
              style={{
                fontFamily: "Georgia, 'Times New Roman', serif",
                fontStyle: "italic",
                fontSize: "1.6rem",
                color: main,
              }}
            >
              {initial}
            </span>
          )}
        </div>
      </div>
    </div>
  );
}

// Signature-ink scaling contract: every uploaded signature renders at the
// SAME ink height (a short "John" and a long "John Content Creator" read as
// the same-size hand), centered on the same anchor point. Width grows with
// the image's aspect ratio up to INK_MAX_W; past that the ink letterboxes
// down inside the fixed box (objectFit contain) rather than overflowing the
// parchment. The apply-form dropzone rejects aspect ratios this box can't
// render sensibly — keep its bounds in sync with INK_MAX_W / INK_H.
const INK_H = 34;
const INK_MAX_W = 150;

/** Rotated signature slot: the creator's real uploaded ink when
 *  `signatureUrl` is present and loads (multiply-blended so white
 *  backgrounds disappear into the parchment), else the cursive name
 *  scrawl when `signed`. Renders nothing when neither applies. */
function SignatureInk({
  name,
  signed,
  signatureUrl,
}: {
  name: string;
  signed: boolean;
  signatureUrl?: string | null;
}) {
  const [errored, setErrored] = useState(false);
  const showImage = !!signatureUrl && !errored;
  if (!showImage && !signed) return null;
  if (showImage) {
    return (
      <div
        aria-hidden="true"
        // Stable hook so the reveal ceremony can ink the signature in (see
        // RevealCeremony's .gh-cert-sig-* rules). Inert everywhere else.
        className="gh-cert-signature"
        style={{
          position: "absolute",
          left: "calc(67% + 12px)",
          bottom: 94,
          width: INK_MAX_W,
          height: INK_H,
          transform: "translateX(-50%) rotate(-12deg)",
          // Blend on the wrapper, not the img: this div's transform creates a
          // stacking context, so a blend on the img would only see the
          // wrapper's transparent background — white ink backgrounds would
          // stay white instead of multiplying into the parchment.
          mixBlendMode: "multiply",
          pointerEvents: "none",
        }}
      >
        <img
          src={signatureUrl ?? undefined}
          alt=""
          onError={() => setErrored(true)}
          style={{
            display: "block",
            width: "100%",
            height: "100%",
            objectFit: "contain",
            opacity: 0.85,
          }}
        />
      </div>
    );
  }
  return (
    <div
      aria-hidden="true"
      className="gh-cert-signature"
      style={{
        position: "absolute",
        left: "58%",
        bottom: 96,
        transform: "rotate(-12deg)",
        fontFamily:
          "'Brush Script MT', 'Segoe Script', 'Snell Roundhand', cursive",
        fontSize: "1.15rem",
        lineHeight: 1,
        color: "rgba(31,20,8,0.68)",
        pointerEvents: "none",
        whiteSpace: "nowrap",
      }}
    >
      {name}
    </div>
  );
}

/**
 * Engraved-parchment certificate of patronage. A fixed 320×452 intrinsic
 * artifact; pass `width` to render it pre-scaled. Non-interactive by design.
 */
export const PatronageCertificate: FC<PatronageCertificateProps> = ({
  tier,
  name,
  serial,
  dossier,
  issued,
  portraitUrl,
  quote,
  condition,
  signed = false,
  signatureUrl,
  width,
  className,
}) => {
  // Case-insensitive palette lookup: normalize the incoming tier to its
  // lowercase key before indexing TIER, so an upstream "Common"/"Premium"
  // (or any casing drift) resolves to the right palette instead of silently
  // falling back to the near-ink common ramp. The prop TS union stays strict;
  // this only hardens the runtime lookup.
  const tierKey = (
    typeof tier === "string" ? tier.toLowerCase() : tier
  ) as PatronageCertificateTier;
  const T = TIER[tierKey] ?? TIER.common;
  const isFounders = tierKey === "founders";
  const cond = Math.max(0, Math.min(1, condition ?? 0));
  const wear = computeWear(serial, cond);
  const initial = Array.from(name.trim())[0]?.toUpperCase() ?? "?";

  // Glow as a FILTER (not a box-shadow) so it follows the torn parchment
  // silhouette (drop-shadow uses the element's alpha) instead of a
  // rectangle — and so nothing rectangular bleeds behind the tears.
  //
  // Tier-graded halo so rarity reads at a glance in the collection grid:
  // Common keeps a quiet, near-neutral drop; the rarer tiers bloom brighter
  // in their accent colour (a tight bright ring + a soft outer wash), with
  // Founder's the warmest/strongest. `${accent}RR` suffixes are hex alpha
  // (b3≈0.70, 66≈0.40, 40≈0.25).
  const dropBase = "drop-shadow(0 8px 16px rgba(14,42,31,0.14))";
  const glowFilter = isFounders
    ? "drop-shadow(0 12px 24px rgba(195,146,53,0.4)) drop-shadow(0 0 20px rgba(233,207,133,0.55)) drop-shadow(0 0 34px rgba(233,207,133,0.3))"
    : tierKey === "common"
      ? `${dropBase} drop-shadow(0 0 12px ${T.accent}40)`
      : `${dropBase} drop-shadow(0 0 16px ${T.accent}b3) drop-shadow(0 0 30px ${T.accent}66)`;
  const parchmentBorderColor = isFounders ? "#c39235" : "rgba(122,90,29,0.45)";

  const mono = "var(--gh-font-mono, 'JetBrains Mono', ui-monospace, monospace)";

  const artifact = (
    <div
      role="img"
      aria-label={`Certificate of patronage for ${name}, ${tier} tier, serial ${serial}`}
      style={{
        position: "relative",
        width: W,
        height: H,
        fontFamily:
          "system-ui, -apple-system, 'Segoe UI', sans-serif",
      }}
    >
      {/* ── Paper: parchment clipped to the torn silhouette (tears are true
          transparency), watermark + smudges inside, edge tint stroked ALONG
          the torn contour, glow via drop-shadow so it hugs the tears. ── */}
      <div aria-hidden="true" style={{ position: "absolute", inset: 0 }}>
        {/* Glow layer — drop-shadow is applied to the CLIPPED parchment child
            below, so the shadow takes the torn silhouette; the glow lives on
            THIS (unclipped) wrapper because CSS applies filter before
            clip-path, so glow + clip on one element would clip the halo away.
            The halo bleeds past the box (wrapper reserves overflow:visible). */}
        <div style={{ position: "absolute", inset: 0, filter: glowFilter }}>
        {/* Parchment body — clip-path carves the tears out to transparency;
            nothing rectangular sits behind, so the missing pieces are truly
            see-through. */}
        <div
          style={{
            position: "absolute",
            inset: 0,
            background:
              "linear-gradient(150deg, var(--gh-parchment-hi,#ece4cc) 0%, var(--gh-parchment,#e7dec4) 45%, var(--gh-parchment-lo,#ddd0ad) 100%)",
            WebkitClipPath: `path('${wear.outline}')`,
            clipPath: `path('${wear.outline}')`,
          }}
        >
          {/* oak watermark */}
          <svg
            viewBox="0 0 100 100"
            style={{
              position: "absolute",
              left: "50%",
              top: "52%",
              width: "70%",
              height: "70%",
              transform: "translate(-50%,-50%)",
              opacity: 0.1,
            }}
          >
            <path d={OAK_PATH} fill="#3a2f1c" />
          </svg>
          {/* smudges + condition vignette (clipped to the torn shape by the
              parent clip-path) */}
          <div
            style={{
              position: "absolute",
              inset: 0,
              pointerEvents: "none",
              background: wear.spots,
              boxShadow: wear.smudgeBoxShadow,
              filter: "blur(0.6px)",
            }}
          />
        </div>
        </div>
        {/* Edge tint + interior cracks. The border is a STROKE of the torn
            outline, so it follows the tears in/out instead of running
            straight across a missing piece. Not clipped — the hairline must
            read on the cut edge itself. */}
        <svg
          viewBox="0 0 320 452"
          preserveAspectRatio="none"
          style={{
            position: "absolute",
            inset: 0,
            width: "100%",
            height: "100%",
            overflow: "visible",
            pointerEvents: "none",
          }}
        >
          <path
            d={wear.outline}
            fill="none"
            stroke={parchmentBorderColor}
            strokeWidth={1}
            strokeLinejoin="round"
          />
          {wear.strokeEls}
        </svg>
      </div>

      {/* ── Content: double-hairline inner frame ──
          Wrapped in a clip to the SAME torn silhouette as the paper, so a
          tear deep enough to reach the inner frame (inset 14) removes the
          hairline too instead of drawing it straight across the missing
          piece. clip-path establishes no stacking context, so the signature
          ink's mix-blend still resolves against the parchment behind. */}
      <div
        style={{
          position: "absolute",
          inset: 0,
          WebkitClipPath: `path('${wear.outline}')`,
          clipPath: `path('${wear.outline}')`,
        }}
      >
      <div
        style={{
          position: "absolute",
          inset: 14,
          // No zIndex here: it would create a stacking context that isolates
          // the signature ink's mix-blend-mode from the parchment layer
          // (white backgrounds would stop disappearing). DOM order already
          // paints this frame above the paper layer.
          border: "1.5px solid rgba(122,90,29,0.65)",
          outline: "1px solid rgba(122,90,29,0.35)",
          outlineOffset: 3,
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          textAlign: "center",
          padding: "20px 16px",
          boxSizing: "border-box",
        }}
      >
        <div
          style={{
            fontFamily: mono,
            fontSize: "0.55rem",
            letterSpacing: "0.28em",
            textTransform: "uppercase",
            color: "#9d7426",
          }}
        >
          Greenhold Registry
        </div>
        <div
          style={{
            fontFamily: "Georgia, 'Times New Roman', serif",
            fontSize: "1.3rem",
            marginTop: 10,
            color: "#1f1408",
          }}
        >
          Certificate of Patronage
        </div>
        <div
          style={{
            width: 60,
            borderTop: `1px solid ${T.accent}`,
            margin: "10px 0 12px",
          }}
        />

        <Portrait
          accent={T.accent}
          main={T.main}
          portraitUrl={portraitUrl}
          initial={initial}
        />

        <div
          style={{
            fontFamily: "Georgia, serif",
            fontSize: "0.95rem",
            fontStyle: "italic",
            marginTop: 9,
            color: T.main,
          }}
        >
          {name}
        </div>

        {quote ? (
          <p
            style={{
              fontSize: "0.66rem",
              color: "rgba(46,35,16,0.8)",
              maxWidth: 210,
              margin: "8px 0 0",
            }}
          >
            {"“"}
            {quote}
            {"”"}
          </p>
        ) : null}

        <SignatureInk name={name} signed={signed} signatureUrl={signatureUrl} />

        {/* bottom mono <dl> — each row only when its value exists */}
        <div
          style={{
            marginTop: "auto",
            display: "grid",
            justifyItems: "center",
            width: "100%",
          }}
        >
          <dl
            style={{
              display: "grid",
              gridTemplateColumns: "auto auto",
              gap: "3px 12px",
              margin: 0,
              fontFamily: mono,
              fontSize: "0.52rem",
              textAlign: "left",
              color: "#3a2f1c",
            }}
          >
            <dt style={{ opacity: 0.55 }}>serial</dt>
            <dd style={{ margin: 0, color: T.main }}>{serial}</dd>
            {dossier ? (
              <>
                <dt style={{ opacity: 0.55 }}>dossier</dt>
                <dd style={{ margin: 0, color: T.main }}>{dossier}</dd>
              </>
            ) : null}
            {issued ? (
              <>
                <dt style={{ opacity: 0.55 }}>issued</dt>
                <dd style={{ margin: 0, color: T.main }}>{issued}</dd>
              </>
            ) : null}
            {condition !== undefined ? (
              <>
                <dt style={{ opacity: 0.55 }}>condition</dt>
                <dd style={{ margin: 0, color: T.main }}>{cond.toFixed(8)}</dd>
              </>
            ) : null}
          </dl>
        </div>
      </div>
      </div>
    </div>
  );

  if (width != null) {
    const scale = width / W;
    return (
      <div
        className={className}
        style={{
          width,
          height: H * scale,
          // NOT overflow:hidden — the tier glow is a drop-shadow that bleeds
          // ~18px beyond the artifact box; clipping it here (the wrapper
          // reserves the exact scaled box) is what made the tier halo vanish
          // on the marketplace / buy-modal cards. `visible` lets it bleed into
          // the card's own padding well, matching MarketPage.dc.html (the
          // design renders the artifact in a 160×226 well with a visible glow).
          overflow: "visible",
        }}
      >
        <div
          style={{
            // Pin to the intrinsic box: a bare block div would stretch to the
            // parent's (already-scaled) `width`, and scaling THAT from top-left
            // balloons an invisible, hit-testable box off to the right/bottom
            // — which swallowed right-side clicks in the collection loupe. At
            // W×H the scaled visual box equals the reserved outer box exactly.
            width: W,
            height: H,
            transform: `scale(${scale})`,
            transformOrigin: "top left",
          }}
        >
          {artifact}
        </div>
      </div>
    );
  }

  return className ? (
    <div className={className}>{artifact}</div>
  ) : (
    artifact
  );
};

export default PatronageCertificate;
